/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { IStateCode } from '../StateCodeStateManager/contracts'

export interface ICoverageDiscoveryResponses {
	responseId: number
	response: IBaseResponse[]
	payerList: string[]
}

export interface IBaseResponseDetail {
	id: number
	payer: IPayer
	hierarchy?: number
}

export interface IIndividual {
	id: number
	customerEntityId: number
	masterIndividualId: number
	isMaster: boolean
	masterPatientKey: string
	firstName: string
	lastName: string
	middleName: string
	gender: string
	dateOfBirth: Date
	socialSecurityNumber: string
	socialSecurityNumberHashed: string
	address1: string
	address2: string
	city: string
	stateCodeId: string
	zip: string
	phoneNumber: string
	mrn: string
	clientId: string
	subscriberId: string
	deleted: boolean
	stateCode: IStateCode
}

export interface IDemographic {
	id: number
	confidenceScore: string
	guarantorId: number
	individualId: number
	guarantorDeceased: boolean
	individualDeceased: boolean
	redFlags: string
	warnings: string
	individual: IIndividual
	guarantor: IIndividual
}

export interface IDemographicIndividualResponse {
	id: number
	confidenceScore: string
	Deceased: boolean
	redFlags: string
	warnings: string
	individual: IIndividual
}

export interface IBaseResponse {
	sections: IBaseSection[]
	eligibilityResponse: IEligibilityResponse
	eligibilityResponseTransaction: ITransactionResponseEligiblity
}

export interface IEligibilityResponse {
	responseDateTime: Date
	responseDateTimeString: string
	payer: IPayer
	raw271: string
	eligibilityBenefits: IEligibilityBenfit[]
	requestErrors: any[]
	createdDate: Date
	subscriber: ISubscriber
}

export interface IEligibilityBenfit {
	authRequired: null | string
	benefitInfoCode: string
	benefitInfoCodeDefinition: string
	benefitTimePeriodCode: null | string
	benefitTimePeriodCodeDefinition: string | null
	coverageLevelCode: string
	coverageLevelCodeDefinition: string | null
	inNetwork: null | string
	insuranceDescription: null | string
	insuranceTypeCode: null | string
	insuranceTypeCodeDefinition: null | string
	monetaryAmount: number
	monetaryAmountString: null | string
	percent: number
	percentString: null | string
	procedureCode: null | IProcedureCode
	procedureCodeEnd: null | string
	procedureModifier1: null | string
	procedureModifier2: null | string
	procedureModifier3: null | string
	procedureModifier4: null | string
	quantity: number
	quantityString: null | string
	quantityQualifierCode: null | string
	quantityQualifierCodeDefinition: null | string
	serviceTypeCode: null | string
	serviceTypeCodeDefinition: null | string
	additionalIdentifications: IAdditionalIdentifiers[]
	benefitAdditionalInfos: any[]
	deliveryPatterns: { [key: string]: null | string }[]
	relatedEntities: IRelatedEntity[]
	textMessages: ITextMessage[]
}

export interface IAdditionalIdentifiers {
	identifier: string
	identifierCode: string
	identifierCodeDefinition: null | string
	description: null | string
}

export interface IDateRange {
	endDate: Date
	endDateString?: null | string
	qualifierCode: null | string
	qualifierCodeDefinition: null | string
	startDate: Date
	startDateString?: null | string
}

export interface IRelatedEntity {
	typeCode: string | null
	typeCodeDefinition: string | null
	qualifierCode: string | null
	qualifierCodeDefinition: string | null
	lastOrOrgName: string | null
	firstName: null | string
	middleName: null | string
	nameSuffix: null | string
	identifier: string | null
	identifierCode: string | null
	identifierCodeDefinition: string | null
	address1: string | null
	address2: string | null
	city: string | null
	state: string | null
	zip: null | string
	providerCodeDefinition: string | null
	providerIdentifier: null | string
	providerIdentifierCode: string | null
	providerIdentifierCodeDefinition: string
	relatedEntityContacts: any[]
}

export interface ITextMessage {
	message: string | null
}

export interface IPayer {
	id: number
	code: string
	name: string
	payerTypeId: number
}
export interface IPcpSectionViewModels {
	cLineList: ICLine[]
	id: number
}

export interface IMcoStatusAlertSectionViewModels {
	cLineList: ICLine[]
	id: number
}
export interface IOtherPayerAlertsSectionViewModels {
	cLineList: ICLine[]
	id: number
}
export interface ICLine {
	sections: ICSection[]
}
export interface ICSection {
	isBold?: boolean
	value: string
}

export interface IProvider {
	typeCode: string
	typeCodeDefinition: string
	qualifierCode: null | string
	qualifierCodeDefinition: string
	lastOrOrgName: string
	identifier: string
	identifierCode: null | string
	identifierCodeDefinition: string
	subscribers: ISubscriber[]
}

export interface ISubscriber {
	firstName: null | string
	middleName: null | string
	lastName: null | string
	address1: null | string
	address2: null | string
	city: null | string
	state: null | string
	zip: null | string
	dateOfBirth: Date
	dateOfBirthString: Date
	gender: null | string
	identifier: null | string
	identifierCode: null | string
	identifierCodeDefinition: null | string
	nameSuffix: null | string
	dependent?: ISubscriber
	eligibilityBenefits?: IEligibilityBenfit[]
	requestErrors?: any[]
	additionalIdentifications: any[]
	traceNumberIdentifications: any[]
	textMessages?: any[]
}

export interface ITransactionResponseEligiblity {
	id: number
	eligibilityStatusId: number
	zollEligibilityStatusId: number
	zollEligibilityStatusDescription: string
	payerId: number
	raw271: string
	payer: IPayer | null
	eligibilityTransactionStatusMessage: null | string
	confidenceScoreReason: null | string
	confidenceScoreText: string
	confidenceScore: IConfidenceScore
	receivedSubscriber: IIndividual
}
export interface IConfidenceScore {
	id: number
	code: string
	description: string
	active: boolean
	deleted: boolean
}
export interface IBaseSection {
	id: string
	loadOrder: number
	displayOrder: number
	isFiltered: boolean
	summary: ISummary
	eligibilitySummary?: IEligibilitySummary
	statusAlerts?: IStatusAlerts
	cEligibilitySummary?: ICEligibilitySummary
	eligibilityResponse?: IEligibilityResponse
	pcpSectionViewModels?: IPcpSectionViewModels[]
	mcoStatusAlertSectionViewModels?: IMcoStatusAlertSectionViewModels[]
	otherPayersSectionViewModels?: IOtherPayerAlertsSectionViewModels[]
}

export interface ICEligibilitySummary {
	eligibilitySummaryStatus: EligibilitySummaryStatus
	eligibilityResponse: IEligibilityResponse
	planName: string | null
	effectiveDate: string | null
	planNetwork: string[] | null
	identifiers: ICEligibilitySummaryIdentifiers[] | null
}

export interface ICEligibilitySummaryIdentifiers {
	item1: string
	item2: string
}

export interface IEligibilitySummary {
	planDescription: string
	planNetwork: any[]
	planDate: string
	dateOfDeath: Date
	partAActives: IPartAActive[]
	partBActives: any[]
	inactivePeriods: any[]
	eligibilitySummaryStatus: EligibilitySummaryStatus
	managedCareCoordinatorName: string
	partAEntitlementReason: string
	partBEntitlementReason: string
}

export interface EligibilitySummaryStatus {
	classColor: string
	largeImage: string
	status: string
	description: string
	toolTip: string
}

export interface IPartAActive {
	item1: Date
	item2: Date
}

export interface IStatusAlerts {
	insurances: IStatusAlertsInsurance[]
	matchedOtherPayers: string | null
}

export interface IStatusAlertsInsurance {
	type?: number
	typeString?: string
	code: string
	definition: string
	insuranceDescription: string
	additionalPayers: IAdditionalPayer[]
	effectiveDateRanges: IPartAActive[]
	coInsurances: ICoInsurance[]
	coPayments: IInpatientCoPayment
	deductibles: IDeductibles
	reserve: IReserve
	descriptions: IDescription[]
	limitations: ILimitation[]
	endOfPeriod2: null | string
}

export interface IAdditionalPayer {
	additionalIdentifiers: IAdditionalIdentifiers[]
	startDate: Date
	endDate: Date
	insuranceTypeCodeDefinition: string
	name: string | null
	address1: string | null
	address2: string | null
	city: string | null
	state: string | null
	zip: string | null
	telephone: null | string
	message: string | null
}

export interface ICoInsurance {
	startDate: Date
	endDate: Date
	percent: number
	message: string
}
export interface IInpatientCoPayment {
	episodeDefinition: string | null
	frequencyDefinition: string | null
	periodDefinition: string | null
	schedules: IInpatientCoPaymentSchedule[]
	episodes: IInpatientCoPaymentSchedule[]
}

export interface IInpatientCoPaymentSchedule {
	startDate: Date
	endDate: Date
	fromPeriod: number
	toPeriod: number
	amount: number
	remaining: number

	periodCode: string

	doeba: Date
	dolba: Date
	isDolbaDoebaPresent: boolean
}

export interface IDeductibles {
	episodeDefinition: string | null
	episodes: IDeductiblesSchedule[]
	schedules: IDeductiblesSchedule[]
}

export interface IDeductiblesSchedule {
	startDate: Date
	endDate: Date
	total: number
	remaining: number
	hasRemaining: boolean
	monetaryAmount: number
}
export interface IDescription {
	startDate: Date
	endDate: Date
	esrdStartDate: Date
	esrdEndDate: Date
	esrdDialysisStartDate: Date
	esrdDialysisEndDate: Date
	esrdTransplantStartDate: Date
	esrdTransplantEndDate: Date
	message: string | null
	amount: number
	isHospice: boolean
	occurences: number
}

export interface ILimitation {
	quantityQualifier: string
	quantityDescription: null | string
	remaining: number
	total: number
	used: number
	message: string | null
	eligibleDate: Date
	effectiveDate: Date
}

export interface IReserve {
	quantityQualifier: string | null
	lifetime: number
	remaining: number
	coPays: ICoPay[]
}

export interface ICoPay {
	startDate: Date
	endDate: Date
	amount: number
	periodDefinition: string | null
}

export interface ISummary {
	index: number
	preferenceKey: string
	title: string
	sections: ISummarySection[]
	selectedSections: string[]
	serviceTypes?: IServiceType[]
	insurances?: IInpatientTypeInsurance[]
	procedureCodes?: IProcedureCode[]
	isHCPCS?: boolean
	inpatientCoPayments?: IInpatientCoPayment[]
	inpatientTypes?: IServiceTypeElement[]
	snfCoPayments?: IInpatientCoPayment[]
	snfTypes?: ISnfType[]
	esrdEffectiveDate?: Date
	esrdEndDate?: Date
	esrdDialysisEffectiveDate?: Date
	esrdDialysisEndDate?: Date
	esrdTransplantEffectiveDate?: Date
	esrdTransplantEndDate?: Date
	homeCertificationDates?: Date[]
	homeRecertificationDates?: Date[]
	homeHealthTypes?: IBloodTypeClass[]
	hospiceTypes?: IBloodTypeClass[]
	deductibleType?: IBloodTypeClass
	cardiacType?: IBloodTypeClass
	occupationalType?: IBloodTypeClass
	physicalType?: IBloodTypeClass
	pulmonaryType?: IBloodTypeClass
	smokingType?: IBloodTypeClass
	acupuncture?: IBloodTypeClass
	bloodType?: IBloodTypeClass
	hospitalSpells?: ISpell[]
	snfSpells?: ISpell[]
	immunizationEligibility?: IImmunizationEligibility
	immunizationHistory?: IImmunizationHistory[]
	immunizationInsurance?: IImmunizationInsurance
}

export interface IBloodTypeClass {
	code: string
	definition: string
	isActive: boolean
	status: string
	insurances: IInpatientTypeInsurance[]
	facilities: IHealthCareFacility[]
	exclusions: IExclusion[]
	createdDate: Date
}
export interface IHealthCareFacility {
	serviceTypeCode: string
	typeDescription: string
	message: string
	startDate: Date
	endDate: Date
	doeba: Date
	dolba: Date
	hospiceDays: string
	contractor: string
	contractorNumber: string
	providerNumber: string
	createdDate: Date
}
export interface IExclusion {
	endDate: Date
	startDate: Date
	remaining: number
	quantityQualifier: string | null
}
export interface IStay {
	doeba: Date
	dolba: Date
	npis: string[]
	dateRanges: IDateRange[]
}
export interface ISpell {
	stays: IStay[]
	spellId: number
	doeba: Date
	dolba: Date
	eligibilityBenifits: IEligibilityBenfit[]
}
export interface IServiceTypeElement {
	code: string
	definition: string
	isActive: boolean
	status: string
	insurances: IInpatientTypeInsurance[]
	exclusions: IExclusion[]
	createdDate: Date
}
export interface IInpatientTypeInsurance {
	code?: null | string
	definition?: null | string
	insuranceDescription?: null | string
	additionalPayers?: IAdditionalPayer[]
	effectiveDateRanges?: IPartAActive[]
	coInsurances?: ICoInsurance[]
	coPayments?: IInpatientCoPayment
	deductibles?: IDeductibles
	reserve?: IReserve
	descriptions?: any[]
	limitations?: any[]
	endOfPeriod2?: null | string
	planName?: string
	insuranceType?: InsuranceType
	insuranceTypeDescription?: string
	coverageLevel?: string
	coverageLevelDescription?: string
	benefits?: IEligibilityBenfit[]
	cInsuranceBenefits?: ICommercialInsuranceDetails[]
}

export interface IProcedureCode {
	code: string
	definition: string
	eligibleProfessional: Date
	eligibleTechnical: Date
	homeCertificationDates: Date[]
	homeRecertificationDates: Date[]
	deductibleTotal: number
	deductibleRemaining: number
	coInsurance: number
	providerNPI: string | null
	pPVDos: IKeyValuePair[]
}
export interface IKeyValuePair {
	key: any
	value: string
}
export interface ISummarySection {
	key: string
	value: string
}

export interface IServiceType {
	code?: string
	definition?: string
	isActive: boolean
	status: string
	insurances: IInpatientTypeInsurance[]
	exclusions?: IExclusion[]
	createdDate?: Date
	serviceCode?: string
	serviceCodeDescription?: string
}

export interface ISnfType {
	code: string
	definition: string
	isActive: boolean
	status: string
	insurances: IInpatientTypeInsurance[]
	exclusions: IExclusion[]
	createdDate: Date
}

export interface IServiceESummary {
	serviceTypes: IServiceTypeElement[]
	procedureCodes: IProcedureCode[]
	isHCPCS: boolean
	index: number
	preferenceKey: string
	title: string
	sections: ISummarySection[]
	selectedSections: string[]
}

export enum InsuranceType {
	Empty = '',
	MB = 'MB',
	Ma = 'MA',
}

export enum IStatusAlertInsuranceType {
	Medicaid,
	Advantage,
	Secondary,
	PartD,
	Supplemental,
	QMB,
}

export interface IBaseResponsesFilterCriteria {
	sortField: string
	sortOrder: string
}
export interface ICommercialInsuranceDetails {
	code: string
	description: string
	lines: ICLine[]
}
export interface IImmunizationEligibility {
	medicarePartBEligible: boolean
}

export interface IImmunizationHistory {
	code: string
	immunizationDate: Date
	renderingNpi: string
}
export interface IImmunizationInsurance {
	code?: null | string
	definition?: null | string
	insuranceDescription?: null | string
	additionalPayers?: IAdditionalPayer[]
	effectiveDateRanges?: IPartAActive[]
	coInsurances?: ICoInsurance[]
	coPayments?: IInpatientCoPayment
	deductibles?: IDeductibles
	reserve?: IReserve
	descriptions?: any[]
	limitations?: any[]
	endOfPeriod2?: null | string
}

export interface ICoverageDiscoveryResponseStateManager {
	payerList: string[]
	eligibilityBenefit: IEligibilityBenfit[]
	responseId: number
	activeResponseEligibility: ITransactionResponseEligiblity | null
	isHcpsPreventativeSection?: boolean
	selectedMedicareServiceCoverageSection: IBaseSection
	selectedMedicareInpatientSection: IBaseSection
	selectedMedicareHomeHospiceSection: IBaseSection
	selectedMedicareTherapyCapSection: IBaseSection
	selectedMedicareEligibilitySection: IBaseSection
	selectedMedicareStatusAlertSection: IBaseSection
	selectedMedicareServiceTypeSection: IBaseSection
	selectedSnfSection: IBaseSection
	selectedPreventativeSection: IBaseSection
	selectedPCPAlertsSection: IBaseSection
	selectedMcoStatusAlert: IBaseSection
	selectedOtherPayerAlert: IBaseSection
	selectedServiceCoverageSection: IBaseSection
	selectedCEligibilitySection: IBaseSection
	selectedBenefitInformationSection: IBaseSection
	selectedCServiceTypeSection: IBaseSection
	selectedCovid19ImmunizationSection: IBaseSection

	getBaseResponseDetails(transactionResponseId: number): Promise<IBaseResponseDetail[]>
	getResponse(id: number): Promise<IBaseResponse>
}
